import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect, } from "react";
import { authService } from "../../../services/authService";
import { PYTHON_BACKEND_URL } from "../../../constants/backend";
const PlanningContext = createContext(undefined);
const plannerWindow = window;
// ============ Helper Functions ============
/** Convert API Plan to PlanListItem for display */
function planToListItem(plan) {
    // Get the latest version to extract slide count
    const latestVersion = plan.plan_versions.length > 0
        ? plan.plan_versions[plan.plan_versions.length - 1]
        : null;
    const planJson = latestVersion?.plan_json;
    // Extract title - priority: plan.title > first few words of prompt > first section name
    let title = plan.title;
    if (!title && planJson?.prompt) {
        // Trim prompt to first few words (max 40 chars)
        const words = planJson.prompt.split(/\s+/).slice(0, 8).join(" ");
        title = words.length > 40 ? words.substring(0, 40) + "..." : words;
    }
    if (!title) {
        title = planJson?.sections?.[0]?.name || "Untitled Plan";
    }
    // Extract slide count
    const slideCount = planJson?.slideCount ||
        planJson?.sections?.reduce((sum, s) => sum + (s.slides?.length || 0), 0) ||
        plan.target_slides ||
        0;
    // Map status
    let status = "draft";
    if (plan.status === "completed")
        status = "completed";
    else if (plan.status === "building")
        status = "building";
    else if (plan.status === "paused")
        status = "paused";
    else if (plan.status === "failed")
        status = "failed";
    // Get style guide display name - prefer explicit name, then derive from path, then default
    const fileName = plan.style_guide_presentation_name ||
        plan.style_guide_presentation_path?.split(/[/\\]/).pop() ||
        "No style guide";
    return {
        id: plan.plan_id,
        title,
        slideCount,
        fileName,
        status,
        createdAt: plan.created_at,
        updatedAt: plan.updated_at,
    };
}
// ============ Provider ============
export const PlanningProvider = ({ children, }) => {
    const [state, setState] = useState({
        isReady: false,
        isAuthReady: authService.isAuthenticated(), // Check initial auth state
        documentId: null,
        adapter: null,
        backendBaseUrl: PYTHON_BACKEND_URL,
        // Plan persistence state
        currentPlanId: null,
        currentPlanVersion: 0,
        plans: [],
        isLoadingPlans: false,
        plansError: null,
        isSaving: false,
    });
    // ============ Auth State Listener ============
    // Listen for auth changes and update isAuthReady
    useEffect(() => {
        const unsubscribe = authService.onAuthChange((isAuthenticated) => {
            console.log("[PlanningContext] Auth state changed:", isAuthenticated);
            setState((prev) => ({ ...prev, isAuthReady: isAuthenticated }));
        });
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, []);
    // ============ Basic Setters ============
    const setReady = useCallback((ready) => {
        setState((prev) => ({ ...prev, isReady: ready }));
    }, []);
    const setdocumentId = useCallback((id) => {
        setState((prev) => ({ ...prev, documentId: id }));
    }, []);
    const setAdapter = useCallback((adapter) => {
        setState((prev) => ({ ...prev, adapter }));
    }, []);
    const setBackendBaseUrl = useCallback((url) => {
        setState((prev) => ({ ...prev, backendBaseUrl: url }));
    }, []);
    const setCurrentPlanId = useCallback((planId) => {
        setState((prev) => ({
            ...prev,
            currentPlanId: planId,
            currentPlanVersion: 0,
        }));
    }, []);
    // ============ Plan Persistence Methods ============
    // These methods use the backendClient exposed on window by ppt-addin
    /** Create a new empty plan shell */
    const createNewPlan = useCallback(async () => {
        if (!plannerWindow.__plansApi_createPlan) {
            console.error("[PlanningContext] Plans API not available on window");
            return null;
        }
        try {
            console.log("[PlanningContext] Creating new plan via backendClient...");
            const data = await plannerWindow.__plansApi_createPlan();
            console.log("[PlanningContext] Plan created:", data.plan_id);
            setState((prev) => ({
                ...prev,
                currentPlanId: data.plan_id,
                currentPlanVersion: 0,
            }));
            return { planId: data.plan_id, outputdocumentId: data.output_deck_id };
        }
        catch (error) {
            console.error("[PlanningContext] Error creating plan:", error);
            return null;
        }
    }, []);
    /** Load all plans (optionally filtered by status) */
    const loadPlans = useCallback(async (status) => {
        if (!plannerWindow.__plansApi_listPlans) {
            console.error("[PlanningContext] Plans API not available on window");
            setState((prev) => ({
                ...prev,
                plansError: "Plans API not available",
            }));
            return;
        }
        setState((prev) => ({ ...prev, isLoadingPlans: true, plansError: null }));
        try {
            console.log("[PlanningContext] Loading plans via backendClient...");
            const data = await plannerWindow.__plansApi_listPlans(status);
            const planItems = (data.plans || []).map((p) => planToListItem(p));
            console.log(`[PlanningContext] Loaded ${planItems.length} plans`);
            setState((prev) => ({
                ...prev,
                plans: planItems,
                isLoadingPlans: false,
            }));
        }
        catch (error) {
            console.error("[PlanningContext] Error loading plans:", error);
            setState((prev) => ({
                ...prev,
                isLoadingPlans: false,
                plansError: error instanceof Error ? error.message : "Failed to load plans",
            }));
        }
    }, []);
    /** Load a single plan with all versions */
    const loadPlan = useCallback(async (planId) => {
        if (!plannerWindow.__plansApi_getPlan) {
            console.error("[PlanningContext] Plans API not available on window");
            return null;
        }
        try {
            console.log(`[PlanningContext] Loading plan via backendClient: ${planId}`);
            const plan = (await plannerWindow.__plansApi_getPlan(planId));
            // Log full plan details for debugging
            console.log(`[PlanningContext] Loaded plan:`, {
                plan_id: plan.plan_id,
                output_deck_id: plan.output_deck_id,
                style_guide_deck_id: plan.style_guide_deck_id,
                style_guide_presentation_path: plan.style_guide_presentation_path,
                style_guide_presentation_name: plan.style_guide_presentation_name,
                target_slides: plan.target_slides,
                status: plan.status,
                versions_count: plan.plan_versions.length,
                created_at: plan.created_at,
                updated_at: plan.updated_at,
            });
            // Log latest version plan_json if available
            if (plan.plan_versions.length > 0) {
                const latestVersion = plan.plan_versions[plan.plan_versions.length - 1];
                console.log(`[PlanningContext] Latest version plan_json:`, latestVersion.plan_json);
            }
            setState((prev) => ({
                ...prev,
                currentPlanId: planId,
                currentPlanVersion: plan.plan_versions.length > 0
                    ? plan.plan_versions[plan.plan_versions.length - 1].version_id
                    : 0,
            }));
            return plan;
        }
        catch (error) {
            console.error("[PlanningContext] Error loading plan:", error);
            return null;
        }
    }, []);
    /** Save a new version of the plan */
    const savePlanVersion = useCallback(async (planId, planJson, targetSlides, label, title) => {
        if (!plannerWindow.__plansApi_updatePlan) {
            console.error("[PlanningContext] Plans API not available on window");
            return null;
        }
        setState((prev) => ({ ...prev, isSaving: true }));
        try {
            console.log(`[PlanningContext] Saving plan version via backendClient for: ${planId}`, { label, title });
            const data = await plannerWindow.__plansApi_updatePlan(planId, planJson, targetSlides, label, title);
            console.log(`[PlanningContext] Saved plan version ${data.version_id} (label: ${data.label})`);
            setState((prev) => ({
                ...prev,
                isSaving: false,
                currentPlanVersion: data.version_id,
            }));
            return {
                versionId: data.version_id,
                timestamp: data.timestamp,
                label: data.label,
            };
        }
        catch (error) {
            console.error("[PlanningContext] Error saving plan:", error);
            setState((prev) => ({ ...prev, isSaving: false }));
            return null;
        }
    }, []);
    /** Revert to a specific version, removing all subsequent versions */
    const revertToVersion = useCallback(async (planId, versionNumber) => {
        if (!plannerWindow.__plansApi_updatePlan) {
            console.error("[PlanningContext] Plans API not available on window");
            return null;
        }
        setState((prev) => ({ ...prev, isSaving: true }));
        try {
            console.log(`[PlanningContext] Reverting plan ${planId} to version ${versionNumber}`);
            // Pass empty plan_json since we're reverting (backend will use existing version)
            const data = await plannerWindow.__plansApi_updatePlan(planId, {}, undefined, undefined, undefined, versionNumber);
            console.log(`[PlanningContext] Reverted to version ${data.version_id}`);
            setState((prev) => ({
                ...prev,
                isSaving: false,
                currentPlanVersion: data.version_id,
            }));
            return { versionId: data.version_id, timestamp: data.timestamp };
        }
        catch (error) {
            console.error("[PlanningContext] Error reverting plan:", error);
            setState((prev) => ({ ...prev, isSaving: false }));
            return null;
        }
    }, []);
    /** Delete a plan permanently */
    const deletePlan = useCallback(async (planId) => {
        console.log("[PlanningContext] deletePlan called with planId:", planId);
        if (!plannerWindow.__plansApi_deletePlan) {
            console.error("[PlanningContext] Plans API not available on window - __plansApi_deletePlan is:", plannerWindow.__plansApi_deletePlan);
            return false;
        }
        try {
            console.log(`[PlanningContext] Calling window.__plansApi_deletePlan for: ${planId}`);
            const data = await plannerWindow.__plansApi_deletePlan(planId);
            console.log("[PlanningContext] API response:", data);
            if (data.success) {
                console.log(`[PlanningContext] Plan deleted successfully, updating local state`);
                // Remove from local state
                setState((prev) => ({
                    ...prev,
                    plans: prev.plans.filter((p) => p.id !== planId),
                    currentPlanId: prev.currentPlanId === planId ? null : prev.currentPlanId,
                }));
            }
            else {
                console.warn("[PlanningContext] API returned success: false");
            }
            return data.success;
        }
        catch (error) {
            console.error("[PlanningContext] Error deleting plan:", error);
            return false;
        }
    }, []);
    /** Parse style guide for a plan */
    const parseStyleGuide = useCallback(async (planId, options) => {
        if (!plannerWindow.__plansApi_parseStyleGuide) {
            console.error("[PlanningContext] Parse Style Guide API not available on window");
            return null;
        }
        try {
            console.log(`[PlanningContext] Parsing style guide for plan: ${planId}`, {
                isLive: options.isLive,
                documentId: options.styleGuidedocumentId,
                path: options.styleGuidePresentationPath,
                name: options.styleGuidePresentationName,
            });
            const result = await plannerWindow.__plansApi_parseStyleGuide(planId, options);
            console.log(`[PlanningContext] Style guide parsed:`, {
                slideCount: result.slide_count,
                sectionsFound: result.sections_found,
                layoutsCount: result.parsed_text?.layouts_used?.length || 0,
                presentationName: result.style_guide_presentation_name,
            });
            return {
                planId: result.plan_id,
                styleGuidedocumentId: result.style_guide_deck_id,
                slideCount: result.slide_count,
                sectionsFound: result.sections_found,
                parsedText: result.parsed_text,
            };
        }
        catch (error) {
            console.error("[PlanningContext] Error parsing style guide:", error);
            return null;
        }
    }, []);
    // ============ Context Value ============
    const value = {
        ...state,
        setReady,
        setdocumentId,
        setAdapter,
        setBackendBaseUrl,
        // Plan persistence methods
        createNewPlan,
        loadPlans,
        loadPlan,
        savePlanVersion,
        revertToVersion,
        deletePlan,
        setCurrentPlanId,
        // Style guide parsing
        parseStyleGuide,
    };
    return (_jsx(PlanningContext.Provider, { value: value, children: children }));
};
export const usePlanning = () => {
    const context = useContext(PlanningContext);
    if (!context) {
        throw new Error("usePlanning must be used within a PlanningProvider");
    }
    return context;
};
